import { useEffect, useState } from 'preact/hooks'
import { getCurrentSessions, getUpcomingSessions, formatRelativeTime, formatTimeRemaining } from '../utils/timeUtils'
import { formatTime } from '../utils/dateUtils'
import { getTorontoHour, formatTorontoTime, getTorontoTimezone, getConferenceAtmosphere, getPeakMoments } from '../utils/torontoTime'

export function LiveDashboard({ 
  sessions, 
  starredSessions, 
  categoryFilter, 
  onSessionClick,
  currentTime,
  isSimulating,
  simulatedTime,
  setIsSimulating,
  setSimulatedTime,
  effectiveTime
}) {
  // New state for enhanced UX
  const [simulationTransition, setSimulationTransition] = useState(false)
  const [selectedTimeOption, setSelectedTimeOption] = useState(null)
  
  // Check if dev mode is enabled via query parameter
  const isDevMode = new URLSearchParams(window.location.search).get('dev') === 'hacker'
  
  // Check if peak time simulation is requested
  const shouldSimulatePeak = new URLSearchParams(window.location.search).get('simulate') === 'peak'

  // Auto-trigger peak time simulation on mount if requested
  useEffect(() => {
    if (shouldSimulatePeak && isDevMode && !isSimulating) {
      // Simulate 11 AM peak time (mid-morning)
      const eventDate = new Date('2025-05-30T09:00:00-04:00') // Event start time
      const peakTime = new Date(eventDate.getTime() + (2 * 60 * 60 * 1000)) // +2 hours = 11 AM
      setSimulatedTime(peakTime)
      setIsSimulating(true)
      
      // Remove the simulate parameter from URL to avoid re-triggering
      const url = new URL(window.location)
      url.searchParams.delete('simulate')
      window.history.replaceState({}, '', url.toString())
    }
  }, [shouldSimulatePeak, isDevMode, isSimulating, setSimulatedTime, setIsSimulating])

  // Use effective time from props instead of local state
  // const effectiveTime is already passed as prop

  // Filter sessions by category first
  const filteredSessions = categoryFilter === 'All' 
    ? sessions 
    : sessions.filter(session => session.category === categoryFilter)

  // Use effective time for session calculations
  const currentSessions = getCurrentSessions(filteredSessions, effectiveTime)
  const upcomingSessions = getUpcomingSessions(filteredSessions, 90, effectiveTime)

  // Enhanced simulation controls with Toronto timezone
  const handleSimulateTime = (timeOption) => {
    setSimulationTransition(true)
    setSelectedTimeOption(timeOption)
    
    const eventDate = new Date('2025-05-30T09:00:00-04:00') // Event start time
    let simulatedDate

    switch (timeOption) {
      case 'event-start':
        simulatedDate = new Date(eventDate)
        break
      case 'mid-morning':
        simulatedDate = new Date(eventDate.getTime() + (2 * 60 * 60 * 1000)) // +2 hours
        break
      case 'lunch-time':
        simulatedDate = new Date(eventDate.getTime() + (4 * 60 * 60 * 1000)) // +4 hours
        break
      case 'afternoon':
        simulatedDate = new Date(eventDate.getTime() + (6 * 60 * 60 * 1000)) // +6 hours
        break
      case 'end-of-day':
        simulatedDate = new Date(eventDate.getTime() + (8 * 60 * 60 * 1000)) // +8 hours
        break
      default:
        simulatedDate = new Date()
    }

    // Use Toronto timezone for consistent display
    const torontoHour = getTorontoHour(simulatedDate)
    console.log(`Simulating ${timeOption}: Toronto time will be ${torontoHour}:00 (${formatTorontoTime(simulatedDate)})`)
    
    setSimulatedTime(simulatedDate)
    setIsSimulating(true)
    
    // Clear transition state after animation
    setTimeout(() => setSimulationTransition(false), 800)
  }

  const handleStopSimulation = () => {
    setIsSimulating(false)
    setSimulatedTime(new Date())
    setSelectedTimeOption(null)
    setSimulationTransition(false)
  }

  // Helper function to get the correct Toronto hour for each simulation option
  const getSimulatedTorontoHour = (timeOption) => {
    // Calculate the same way as handleSimulateTime to ensure consistency
    const eventDate = new Date('2025-05-30T09:00:00-04:00')
    let simulatedDate

    switch (timeOption) {
      case 'event-start':
        simulatedDate = new Date(eventDate)
        break
      case 'mid-morning':
        simulatedDate = new Date(eventDate.getTime() + (2 * 60 * 60 * 1000))
        break
      case 'lunch-time':
        simulatedDate = new Date(eventDate.getTime() + (4 * 60 * 60 * 1000))
        break
      case 'afternoon':
        simulatedDate = new Date(eventDate.getTime() + (6 * 60 * 60 * 1000))
        break
      case 'end-of-day':
        simulatedDate = new Date(eventDate.getTime() + (8 * 60 * 60 * 1000))
        break
      default:
        return -1
    }
    
    return getTorontoHour(simulatedDate)
  }

  // Get current conference atmosphere
  const atmosphere = getConferenceAtmosphere(effectiveTime)
  const currentTorontoTime = formatTorontoTime(effectiveTime)
  const currentTimezone = getTorontoTimezone(effectiveTime)
  const currentTorontoHour = getTorontoHour(effectiveTime)

  const CompactSessionCard = ({ session, isUpcoming = false }) => {
    const isStarred = starredSessions.includes(session.id)
    
    return (
      <div
        onClick={() => onSessionClick && onSessionClick(session.id)}
        className={`
          backdrop-blur-sm border rounded-lg p-3 cursor-pointer
          transition-all duration-200 transform hover:scale-[1.02]
          ${isStarred 
            ? 'bg-editions-orange/10 border-editions-orange/40 ring-1 ring-editions-orange/30' 
            : 'bg-white/5 border-white/10 hover:bg-white/10 hover:border-editions-purple/30'
          }
        `}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h4 className={`font-medium text-sm leading-tight truncate ${
                !isUpcoming ? 'text-white live-session-animated' : 'text-white'
              }`}>
                {session.title}
              </h4>
              {!isUpcoming && (
                <div className="flex-shrink-0 flex items-center gap-1" title="Live now">
                  <div className="w-2 h-2 bg-editions-green rounded-full animate-pulse"></div>
                  <span className="text-xs text-editions-green font-medium">LIVE</span>
                </div>
              )}
              {isStarred && (
                <div className="flex-shrink-0" title="In your plan">
                  <svg className="w-4 h-4 text-editions-orange fill-current" viewBox="0 0 24 24">
                    <path d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                  </svg>
                </div>
              )}
            </div>
            <div className="text-xs text-gray-400 mb-1">
              📍 {session.location}
            </div>
            <div className="text-xs text-editions-orange">
              {isUpcoming 
                ? formatRelativeTime(session.startTime, effectiveTime) 
                : `Ends ${formatTime(session.endTime)}${!isUpcoming ? ` (${formatTimeRemaining(session, effectiveTime)})` : ''}`
              }
            </div>
            {session.speakers && session.speakers.length > 0 && (
              <div className="text-xs text-gray-300 mt-1 truncate">
                👤 {session.speakers[0].name}
                {session.speakers.length > 1 && ` +${session.speakers.length - 1} more`}
              </div>
            )}
          </div>
          <div className={`text-xs px-2 py-1 rounded-full font-medium ${
            session.category === 'Dev mode' 
              ? 'bg-editions-green/20 text-editions-green'
              : session.category === 'Biz mode'
              ? 'bg-editions-purple/20 text-editions-purple-light'
              : 'bg-gray-600/20 text-gray-300'
          }`}>
            {session.category}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="mb-6 space-y-4">
      {/* Enhanced Simulation Controls - Only show in dev mode */}
      {isDevMode && (
        <div className={`bg-editions-dark-light/50 rounded-xl p-4 border transition-all duration-300 ${
          isSimulating ? 'border-editions-orange/40' : 'border-editions-orange/20'
        }`}>
          {/* Current Time Context */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-2">
                <span>🧪</span>
                <h4 className="text-white font-medium text-sm">
                  Time Simulation 
                  {isSimulating && <span className="text-editions-orange ml-2">(Active)</span>}
                </h4>
                <span className="text-xs text-gray-400">(Dev Mode)</span>
              </div>
              
              {/* Enhanced Time Display */}
              <div className="text-xs text-gray-300 flex items-center justify-between">
                <span>📍 Toronto Time: <span className="text-editions-orange font-medium">{currentTorontoTime} {currentTimezone}</span></span>
                <span>🎯 Energy: <span className="text-editions-purple-light capitalize">{atmosphere.energy}</span></span>
              </div>
              
              {/* Simulation Context */}
              {isSimulating && selectedTimeOption && (
                <div className="text-xs text-editions-orange bg-editions-orange/10 px-2 py-1 rounded-md inline-block mt-1">
                  🕒 Simulating: {(() => {
                    const labels = {
                      'event-start': 'Conference Opening (9:00 AM)',
                      'mid-morning': 'Peak Morning Energy (11:00 AM)', 
                      'lunch-time': 'Lunch Networking (1:00 PM)',
                      'afternoon': 'Deep Dive Sessions (3:00 PM)',
                      'end-of-day': 'Wrap-up Time (5:00 PM)'
                    }
                    return labels[selectedTimeOption] || 'Custom Time'
                  })()} Toronto Time
                </div>
              )}
            </div>
            
            {isSimulating && (
              <button
                onClick={handleStopSimulation}
                className="text-xs bg-editions-orange/20 text-editions-orange px-3 py-1 rounded-full 
                           hover:bg-editions-orange/30 transition-colors flex items-center gap-1"
              >
                ⏹️ Stop Simulation
              </button>
            )}
          </div>
          
          {/* Enhanced Simulation Timeline */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
            {[
              { key: 'event-start', label: 'Conference Opens', time: '9:00 AM', icon: '🎉', energy: 'building' },
              { key: 'mid-morning', label: 'Peak Energy', time: '11:00 AM', icon: '🔥', energy: 'peak' },
              { key: 'lunch-time', label: 'Networking', time: '1:00 PM', icon: '🤝', energy: 'social' },
              { key: 'afternoon', label: 'Deep Dives', time: '3:00 PM', icon: '⚡', energy: 'focused' },
              { key: 'end-of-day', label: 'Wrap-up', time: '5:00 PM', icon: '🌅', energy: 'winding' }
            ].map(({ key, label, time, icon, energy }) => (
              <button
                key={key}
                onClick={() => handleSimulateTime(key)}
                className={`text-xs p-2 rounded-lg transition-all duration-300 transform ${
                  (() => {
                    const expectedTorontoHour = getSimulatedTorontoHour(key)
                    const actualTorontoHour = getTorontoHour(simulatedTime)
                    const isActive = isSimulating && expectedTorontoHour === actualTorontoHour
                    return isActive
                      ? 'bg-editions-orange/30 text-editions-orange border border-editions-orange/50 scale-105 shadow-lg shadow-editions-orange/20'
                      : 'bg-white/5 text-gray-300 hover:bg-white/10 hover:text-white border border-white/10 hover:scale-105'
                  })()
                } ${simulationTransition ? 'animate-pulse' : ''}`}
                              >
                  <div className="flex items-center justify-center gap-1 mb-1">
                    <span className="text-sm">{icon}</span>
                    <div className="font-medium text-xs">{label}</div>
                  </div>
                  <div className="text-xs opacity-70">{time}</div>
                </button>
            ))}
          </div>

          {/* Custom Time Input */}
          <div className="mt-3 pt-3 border-t border-white/10">
            <div className="flex flex-col sm:flex-row items-center gap-3">
              <label className="text-xs text-gray-400 flex-shrink-0">Custom Time:</label>
              <div className="flex items-center gap-2 flex-1">
                <input
                  type="datetime-local"
                  className="bg-white/5 border border-white/20 rounded-lg px-3 py-1 text-xs text-white
                           focus:border-editions-orange/50 focus:outline-none focus:ring-1 focus:ring-editions-orange/30
                           flex-1 min-w-0"
                  onChange={(e) => {
                    if (e.target.value) {
                      const customTime = new Date(e.target.value)
                      setSimulatedTime(customTime)
                      setIsSimulating(true)
                    }
                  }}
                />
                <button
                  onClick={() => {
                    const now = new Date()
                    const input = document.querySelector('input[type="datetime-local"]')
                    if (input) {
                      const localDateTime = new Date(now.getTime() - now.getTimezoneOffset() * 60000).toISOString().slice(0, 16)
                      input.value = localDateTime
                      setSimulatedTime(now)
                      setIsSimulating(true)
                    }
                  }}
                  className="text-xs bg-white/10 hover:bg-white/20 text-gray-300 hover:text-white 
                           px-2 py-1 rounded border border-white/20 hover:border-white/40 transition-colors"
                >
                  Now
                </button>
              </div>
            </div>
          </div>

          {/* Contextual Peak Moment - Only show for selected time */}
          {isSimulating && selectedTimeOption === 'mid-morning' && (
            <div className="mt-3 pt-3 border-t border-white/10">
              <div className="text-xs bg-editions-orange/10 text-editions-orange px-2 py-1 rounded-md inline-flex items-center gap-1">
                <span>🔥</span>
                <span>Busiest Period - Peak Conference Energy</span>
              </div>
            </div>
          )}

          {/* Social Sharing */}
          <div className="mt-3 pt-3 border-t border-white/10">
            <div className="flex items-center justify-between">
              <div className="text-xs text-gray-400">Share Your Conference Experience</div>
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    const text = `🚀 Exploring Shopify Editions .DEV! Currently experiencing ${atmosphere.description} at ${currentTorontoTime} Toronto time. ${currentSessions.length} sessions happening now! #ShopifyEditions #EditionsVibes`
                    const url = window.location.href
                    if (navigator.share) {
                      navigator.share({ title: 'Editions Vibes', text, url })
                    } else {
                      navigator.clipboard.writeText(`${text} ${url}`)
                      // You could add a toast notification here
                    }
                  }}
                  className="text-xs bg-white/10 hover:bg-white/20 text-gray-300 hover:text-white 
                           px-2 py-1 rounded border border-white/20 hover:border-white/40 transition-colors
                           flex items-center gap-1"
                >
                  <span>📱</span>
                  <span>Share</span>
                </button>
                <button
                  onClick={() => {
                    const screenshot = `Conference Energy: ${atmosphere.description} | ${currentSessions.length} Live Sessions | ${upcomingSessions.length} Coming Up | ${formatTorontoTime(effectiveTime)} Toronto`
                    navigator.clipboard.writeText(screenshot)
                  }}
                  className="text-xs bg-white/10 hover:bg-white/20 text-gray-300 hover:text-white 
                           px-2 py-1 rounded border border-white/20 hover:border-white/40 transition-colors
                           flex items-center gap-1"
                >
                  <span>📋</span>
                  <span>Copy Status</span>
                </button>
              </div>
            </div>
          </div>

          {isSimulating && (
            <div className="mt-3 pt-3 border-t border-white/10 text-center">
              <div className="text-xs text-gray-400">
                Simulating: <span className="text-editions-orange font-medium">
                  {simulatedTime.toLocaleDateString()} {formatTorontoTime(simulatedTime)} {getTorontoTimezone(simulatedTime)}
                </span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Conference Atmosphere Indicator */}
      {isDevMode && (
        <div className={`bg-gradient-to-r ${atmosphere.color} backdrop-blur-lg border border-white/20 rounded-xl p-3 transition-all duration-500`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="text-xl">🌟</div>
              <div>
                <div className="text-white text-sm font-medium">Conference Energy</div>
                <div className="text-xs text-gray-300 capitalize">{atmosphere.description}</div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <div className="text-xs text-gray-400">Density</div>
              <div className="w-16 h-2 bg-white/20 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-editions-orange transition-all duration-1000"
                  style={{ width: `${atmosphere.density}%` }}
                ></div>
              </div>
              <div className="text-xs text-editions-orange font-medium">{atmosphere.density}%</div>
            </div>
          </div>
        </div>
      )}

      {/* Happening Now Section */}
      <div className="bg-gradient-to-r from-editions-purple/10 to-editions-green/10 
                      backdrop-blur-lg border border-editions-purple/30 rounded-xl p-4">
        <div className="flex items-center gap-2 mb-3">
          <span className="text-lg">🔴</span>
          <h3 className="text-white font-semibold live-session-animated">Happening Now</h3>
          <div className="flex-1 h-px bg-editions-purple/30"></div>
          <span className="text-xs text-gray-400">
            {formatTorontoTime(effectiveTime)} {getTorontoTimezone(effectiveTime)}
            {isSimulating && <span className="ml-1 text-editions-orange">(Simulated)</span>}
          </span>
        </div>
        
        {currentSessions.length > 0 ? (
          <div className="space-y-2 max-h-80 overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-white/20 scrollbar-track-transparent">
            {currentSessions.map(session => (
              <CompactSessionCard 
                key={session.id} 
                session={session} 
                isUpcoming={false}
              />
            ))}
            {/* Show count of starred sessions in this section */}
            {currentSessions.some(s => starredSessions.includes(s.id)) && (
              <div className="text-xs text-editions-orange text-center mt-2 pt-2 border-t border-white/10">
                ⭐ {currentSessions.filter(s => starredSessions.includes(s.id)).length} of these sessions are in your plan
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-4 text-gray-400">
            <span className="text-2xl block mb-2">⏸️</span>
            <p className="text-sm">No sessions currently in progress</p>
            {categoryFilter !== 'All' && (
              <p className="text-xs mt-1">in {categoryFilter} category</p>
            )}
            {isDevMode && isSimulating && (
              <p className="text-xs mt-1 text-editions-orange">
                Try different simulation times to see live sessions
              </p>
            )}
          </div>
        )}
      </div>

      {/* Coming Up Next Section */}
      <div className="bg-gradient-to-r from-editions-orange/10 to-editions-purple/10 
                      backdrop-blur-lg border border-editions-orange/30 rounded-xl p-4">
        <div className="flex items-center gap-2 mb-3">
          <span className="text-lg">⏰</span>
          <h3 className="text-white font-semibold">Coming Up Next</h3>
          <div className="flex-1 h-px bg-editions-orange/30"></div>
          <span className="text-xs text-gray-400">Next 90 minutes</span>
        </div>
        
        {upcomingSessions.length > 0 ? (
          <div className="space-y-2 max-h-80 overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-white/20 scrollbar-track-transparent">
            {upcomingSessions.map(session => (
              <CompactSessionCard 
                key={session.id} 
                session={session} 
                isUpcoming={true}
              />
            ))}
            {/* Show count of starred sessions in this section */}
            {upcomingSessions.some(s => starredSessions.includes(s.id)) && (
              <div className="text-xs text-editions-orange text-center mt-2 pt-2 border-t border-white/10">
                ⭐ {upcomingSessions.filter(s => starredSessions.includes(s.id)).length} of these sessions are in your plan
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-4 text-gray-400">
            <span className="text-2xl block mb-2">📅</span>
            <p className="text-sm">No sessions coming up soon</p>
            {categoryFilter !== 'All' && (
              <p className="text-xs mt-1">in {categoryFilter} category</p>
            )}
            {isDevMode && isSimulating && (
              <p className="text-xs mt-1 text-editions-orange">
                Try different simulation times to see upcoming sessions
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  )
} 